import * as React from 'react';
import { makeStyles, IconButton } from '@material-ui/core';
import { blue, neutral } from '../theme/colors';
import { Clear, Search } from '@material-ui/icons';
import { fontFamilies } from '../theme/text';
import clsx from 'clsx';

interface Props {
  /** Value of the text input within the search bar */
  value: string;
  /** Function when value is changed (entered by user) */
  updateValue: (val: string) => void;
  /** Initial placeholder when no value is present */
  placeholder: string;
  /** Handler for form submit (on enter or clicking icon) */
  submitHandler?: () => void;
  /** Disable the input and button, defaults to false */
  disabled?: boolean;
  /** extra props for the inner input element */
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  /** if false, don't allow submitting the form, display icon instead of submit button */
  allowSubmitButton?: boolean;
  /** Show X button to clear current search when value is present */
  showClearButton?: boolean;
  /** Add or override root styles */
  className?: string;
}

const useStyles = makeStyles({
  root: {
    boxSizing: 'border-box',
    border: '1px solid #115a80',
    borderRadius: '15px',
    height: '32px',
    width: '268px',
    '&:focus-within': {
      border: `1px solid ${blue[300]}`
    }
  },
  form: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginLeft: '5px',
    marginRight: '5px',
    height: '100%',
    '&.disabled': {
      backgroundColor: 'rgba(239, 239, 239, 0.3)',
      cursor: 'not-allowed'
    },
    '& .MuiIconButton-root:hover': {
      backgroundColor: blue[100]
    },
    '& input': {
      boxSizing: 'border-box',
      border: 'none',
      width: '100%',
      fontFamily: fontFamilies.heading,
      fontSize: '14px',
      color: blue[800],
      cursor: 'pointer',
      outlineWidth: 0,
      '&:disabled': {
        cursor: 'not-allowed',
        backgroundColor: 'transparent'
      }
    },
    '& input::placeholder': {
      color: neutral[500]
    },
    '& svg': {
      fontSize: '20px',
      color: blue[600],
      '&.icon-only': {
        padding: 3,
        color: neutral[600]
      }
    }
  }
});

/**
 * SearchBar, controlled component for search bar used with tables and help documents
 */
const SearchBar = ({
  value,
  updateValue,
  placeholder,
  submitHandler,
  disabled = false,
  inputProps,
  allowSubmitButton = true,
  showClearButton,
  className
}: Props) => {
  const classes = useStyles();
  const inputRef = React.useRef<HTMLInputElement>();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitHandler && submitHandler();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateValue(event.target.value);
  };

  const handleClear = () => {
    updateValue('');
    inputRef.current.focus();
  };

  return (
    <div className={clsx(classes.root, className)}>
      <form
        className={clsx(classes.form, disabled && 'disabled')}
        onSubmit={(event) => handleSubmit(event)}
      >
        {allowSubmitButton ? (
          <IconButton aria-label="search" size="small" type="submit" disabled={disabled}>
            <Search />
          </IconButton>
        ) : (
          <Search className="icon-only" />
        )}
        <input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          {...inputProps}
        />
        {showClearButton && value.length > 0 && (
          <IconButton size="small" onClick={handleClear} disabled={disabled}>
            <Clear />
          </IconButton>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
